import { AfterViewInit, Component, ContentChildren, ElementRef, HostBinding, Input, NgZone, OnDestroy, OnInit, QueryList, ViewEncapsulation, inject } from '@angular/core';
import { TidySmallButtonComponent } from 'src/tidy-ui-components/components/buttons/small-button/small-button.component';
import { WindowService } from 'src/shared/providers/window.service';

@Component({
    selector: 'tidy-desktop-header',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./desktop-header.scss'],
    template: `
    <ng-content></ng-content>
    @if (isLoading) {
      <div class="loading-bar-container">
        <div class="loading-bar"></div>
      </div>
    }
  `,
    standalone: false
})

export class DesktopHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ContentChildren(TidySmallButtonComponent, { descendants: true }) headerButtons: QueryList<TidySmallButtonComponent>;
  @Input() isLoading: boolean;

  // One-shot entrance: the header items fade in (matching the desktop nav)
  // only on the app's initial load / refresh — the first header created in the
  // session plays it, and page navigations after that show headers instantly.
  // A short burst window lets headers created together at boot all play.
  @HostBinding('class.page-entrance') entranceActive = false;
  private static entranceClaimedAt = 0;
  private windowService = inject(WindowService);

  private fullWidthIconOnly = 0;
  private fullWidthWithText = 0;
  private resizeObserver: ResizeObserver;

  constructor(
    private el: ElementRef,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    if (!this.windowService?.isDesktopRes) {
      return;
    }
    const now = Date.now();
    // The burst window only covers sibling headers mounting together on the
    // initial page — it must stay shorter than any possible user navigation
    if (DesktopHeaderComponent.entranceClaimedAt && now - DesktopHeaderComponent.entranceClaimedAt > 400) {
      return;
    }
    if (!DesktopHeaderComponent.entranceClaimedAt) {
      DesktopHeaderComponent.entranceClaimedAt = now;
    }
    this.entranceActive = true;
    setTimeout(() => this.entranceActive = false, 1500);
  }

  ngAfterViewInit() {
    const wrappableContainer = this.el.nativeElement.querySelector('.wrappable-container');
    if (!wrappableContainer) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        this.checkOverflow(wrappableContainer);
      });
    });
    this.resizeObserver.observe(wrappableContainer);
    setTimeout(() => this.checkOverflow(wrappableContainer), 0);
  }

  private checkOverflow(container: HTMLElement) {
    const isIconOnly = this.headerButtons?.first?.iconOnly ?? false;
    const isWrapping = container.classList.contains('allow-wrap');
    if (!isIconOnly) {
      this.fullWidthWithText = container.scrollWidth;
      if (container.scrollWidth > container.clientWidth) {
        this.headerButtons?.forEach(button => button.iconOnly = true);
      }
    } else if (!isWrapping) {
      this.fullWidthIconOnly = container.scrollWidth;
      if (container.scrollWidth > container.clientWidth) {
        container.classList.add('allow-wrap');
      } else if (container.clientWidth >= this.fullWidthWithText + 10) {
        this.headerButtons?.forEach(button => button.iconOnly = false);
      }
    } else {
      if (container.clientWidth >= this.fullWidthIconOnly + 10) {
        container.classList.remove('allow-wrap');
      }
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
